import { useMutation } from '@apollo/client/react';
import { ADD_PHONE_NUMBERS_MUTATION } from '../../integrations/allkort/graphql/gql/PhoneMutations';
import { AddPhoneNumbersResponse, PhoneTypeId } from '../../integrations/allkort/graphql/interface/Phones';

/**
 * useAddPhoneNumber
 *
 * Custom hook for adding a phone number to the customer's account.
 *
 * @example
 * ```tsx
 * const { addPhoneNumber, loading } = useAddPhoneNumber();
 *
 * const handleAdd = async () => {
 *   const result = await addPhoneNumber({
 *     customerId: currentCustomer.id,
 *     phoneNo: '+46701234567',
 *   });
 *   if (result) console.log('Added phone id:', result[0]?.id);
 * };
 * ```
 */
export const useAddPhoneNumber = () => {
	const [addPhoneNumbersMutation, { loading, error, data }] = useMutation<AddPhoneNumbersResponse>(
		ADD_PHONE_NUMBERS_MUTATION
	);

	/**
	 * Add a phone number to the customer's account
	 * @param customerId  - The customer's ID
	 * @param phoneNo     - International format, e.g. "+46701234567"
	 * @param description - Label for the number (defaults to "Huvudtelefonnummer")
	 * @param phoneType   - Phone type enum (defaults to 'MAIN')
	 * @param sortOrder   - Display sort order (defaults to 1)
	 * @returns The added phone number list, or throws on failure
	 */
	const addPhoneNumber = async ({
		customerId,
		phoneNo,
		description = 'Huvudtelefonnummer',
		phoneType = 'MAIN',
		sortOrder = 1,
	}: {
		customerId: number
		phoneNo: string
		description?: string
		phoneType?: PhoneTypeId
		sortOrder?: number
	}) => {
		try {
			const result = await addPhoneNumbersMutation({
				variables: { customerId, description, phoneNo, phoneType, sortOrder },
			});

			if (result.error) {
				const apolloError = result.error as any;
				const message = apolloError.graphQLErrors?.[0]?.message ?? apolloError.message;
				console.error('[useAddPhoneNumber] GraphQL error:', message);
				throw new Error(message);
			}

			return result.data?.addPhoneNumbers?.phoneNumber ?? null;
		} catch (err) {
			// Re-throw so the caller can inspect and display the message
			throw err;
		}
	};

	return { addPhoneNumber, loading, error, data };
};
